import * as React from 'react';
import { TCheckout } from './Checkout.types';
import './Checkout.styled.scss';
import { CheckoutProgress } from './components/CheckoutProgress/CheckoutProgress';
import { CartReview } from './components/CartReview/CartReview';
import { ShippingForm } from './components/ShippingForm/ShippingForm';
import { PaymentMethod } from './components/PaymentMethod/PaymentMethod';
import {
  TCheckoutStep,
  TShippingFormData,
  TPaymentMethod,
} from './Checkout.types';
import { DELIVERY_METHODS } from './Checkout.constants';
import { useNavigate } from 'react-router';
import { APP_ROUTES } from '../../constants/routes';
import { useLocalCart } from '../../hooks/useLocalCart';

export const Checkout: TCheckout = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState<TCheckoutStep>('cart');
  const {
    items: cartItems,
    total: subtotal,
    updateQuantity,
    removeItem,
    clearAll,
  } = useLocalCart();
  const [shippingData, setShippingData] =
    React.useState<TShippingFormData | null>(null);
  const [paymentMethod, setPaymentMethod] =
    React.useState<TPaymentMethod | null>(null);

  const shippingCost = React.useMemo(() => {
    if (!shippingData) return 0;
    const method = DELIVERY_METHODS.find(
      (m) => m.id === shippingData.deliveryMethod
    );
    return method?.price || 0;
  }, [shippingData]);

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + delta);
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
    if (cartItems.length === 1) {
      // If last item removed, redirect to home
      navigate(APP_ROUTES.HOME);
    }
  };

  const handleCartContinue = () => {
    if (cartItems.length === 0) return;
    setCurrentStep('shipping');
  };

  const handleShippingContinue = (data: TShippingFormData) => {
    setShippingData(data);
    setCurrentStep('payment');
  };

  const handleShippingBack = () => {
    setCurrentStep('cart');
  };

  const handlePaymentMethodSelect = (method: TPaymentMethod) => {
    setPaymentMethod(method);
  };

  const handlePaymentComplete = (method: TPaymentMethod) => {
    // Here you would typically send the order to the backend
    console.log('Order completed:', {
      items: cartItems,
      shipping: shippingData,
      payment: method,
      total: subtotal + shippingCost,
    });

    // Clear cart after successful order
    clearAll();

    // Redirect to order confirmation page or home
    // navigate(APP_ROUTES.ORDER_CONFIRMATION);
    alert('Order placed successfully! (This is a demo)');
    navigate(APP_ROUTES.HOME);
  };

  const handlePaymentBack = () => {
    setCurrentStep('shipping');
  };

  if (cartItems.length === 0 && currentStep === 'cart') {
    return (
      <div className="checkout">
        <div className="checkout__container">
          <div className="checkout__empty">
            <h2>Your cart is empty</h2>
            <p>Add items to your cart to continue</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="checkout__container">
        <CheckoutProgress currentStep={currentStep} />

        <div className="checkout__content">
          {currentStep === 'cart' && (
            <CartReview
              items={cartItems}
              onQuantityChange={handleQuantityChange}
              onRemoveItem={handleRemoveItem}
              onContinue={handleCartContinue}
            />
          )}

          {currentStep === 'shipping' && (
            <ShippingForm
              initialData={shippingData || undefined}
              onContinue={handleShippingContinue}
              onBack={handleShippingBack}
            />
          )}

          {currentStep === 'payment' && (
            <PaymentMethod
              selectedMethod={paymentMethod}
              onMethodSelect={handlePaymentMethodSelect}
              onComplete={handlePaymentComplete}
              onBack={handlePaymentBack}
              subtotal={subtotal}
              shippingCost={shippingCost}
            />
          )}
        </div>
      </div>
    </div>
  );
};
